import { FC, useState } from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import PageContainer from 'components/PageContainer';
import PageContent from 'components/PageContent';
import PageSection from 'components/PageSection';
import Heading from 'components/Heading';
import useVehicle from 'hooks/lp/data/vehicle/useVehicle';
import { useLayoutContext } from 'contexts/LayoutContext';
import { Row } from 'components/Layout';
import { CurrencyExchange, Money } from '@mui/icons-material';
import { AppDatePicker, FieldRow } from 'components/FormElements';
import CurrencyRate from 'components/CurrencyRate';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { CurrencyRateDetails } from 'further-types/lp/transaction';
import Spacing from 'components/Spacing';
import BankReconciliationWarningBox from '../BankReconciliationWarning';
import PayablesOrReceivables from './PayablesOrReceivables';
import { CalendarDay, pluralize } from 'further-ui/utils';
import Cash from './Cash';
import { useDebounce } from 'usehooks-ts';
import useEqualisations from 'hooks/lp/data/equalisations/useEqualisations';

enum PostUnrealisedFXGainsTab {
  PayablesOrReceivables = 'payablesOrReceivables',
  Cash = 'cash',
}

const tabsData = {
  [PostUnrealisedFXGainsTab.PayablesOrReceivables]: {
    title: 'Payables or receivables',
    icon: <CurrencyExchange />,
  },
  [PostUnrealisedFXGainsTab.Cash]: {
    title: 'Cash',
    icon: <Money />,
  },
};

const PostUnrealisedFXGains: FC = () => {
  const { selectedVehicleId } = useLayoutContext();
  const { vehicle } = useVehicle(selectedVehicleId);

  const [viewDate, setViewDate] = useState<Date | null>(new Date());
  const debouncedViewDate = useDebounce(viewDate, 800);
  const [currencyRates, setCurrencyRates] = useState<
    Array<[CurrencyCode, CurrencyRateDetails]>
  >([]);
  const [currencyRateOverrides, setCurrencyRateOverrides] = useState<
    Map<CurrencyCode, CurrencyRateDetails>
  >(new Map());
  const [activeTab, setActiveTab] = useState<PostUnrealisedFXGainsTab>(
    PostUnrealisedFXGainsTab.PayablesOrReceivables,
  );
  const [lastPostingDate, setLastPostingDate] = useState<CalendarDay | null>(
    null,
  );

  const { latestApprovedEqualisationDate } = useEqualisations(
    selectedVehicleId ?? '',
  );

  const handleTabChange = (
    _: unknown,
    selectedTab: PostUnrealisedFXGainsTab,
  ) => {
    setActiveTab(selectedTab);
  };

  const handleCurrenciesLoaded = (
    currencies: Array<[CurrencyCode, CurrencyRateDetails]>,
  ) => {
    setCurrencyRates(currencies);
  };

  const handleLastPostingDateLoaded = (date: CalendarDay | null) => {
    setLastPostingDate(date);
  };

  if (!vehicle) {
    return null;
  }

  return (
    <PageContainer heading="Automated Transactions: Post unrealised FX gains">
      <PageContent withSpacing>
        <PageSection title="Post unrealised FX gains" withSpacing>
          <Spacing size="sm">
            <FieldRow title="Select view date" centerTitle>
              <AppDatePicker
                placeholder="Select view date"
                value={viewDate}
                onChange={(date) => {
                  setViewDate(date);
                }}
                disableFuture
                minDate={
                  lastPostingDate?.toUTCDate() ?? latestApprovedEqualisationDate
                }
                disabled={
                  activeTab !== PostUnrealisedFXGainsTab.PayablesOrReceivables
                }
              />
            </FieldRow>

            <Box>
              <Typography variant="h6" color="text.secondary">
                {pluralize('Exchange rate', currencyRates.length ?? 0)}:
              </Typography>
              {!!vehicle &&
                currencyRates.map(([currencyCode, fxRate], index) => (
                  <CurrencyRate
                    key={`${currencyCode}-${index}`}
                    fxRate={fxRate}
                    currency={currencyCode}
                    vehicle={vehicle}
                    isFetchingRate={false}
                    onRateChange={(rateDetails) => {
                      setCurrencyRateOverrides((prev) => {
                        const newMap = new Map(prev);
                        newMap.set(currencyCode, rateDetails);
                        return newMap;
                      });
                    }}
                  />
                ))}
            </Box>
          </Spacing>
        </PageSection>

        <PageSection withSpacing>
          <Tabs
            showDivider
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
          >
            {Object.keys(tabsData).map((tab) => (
              <Tab
                key={tab}
                value={tab}
                label={
                  <Row centered spacing="xs">
                    {tabsData[tab].icon}
                    <Heading variant="h5" noMargin>
                      {tabsData[tab].title}
                    </Heading>
                  </Row>
                }
              />
            ))}
          </Tabs>
          <BankReconciliationWarningBox
            date={debouncedViewDate}
            warningText=" There are transactions awaiting bank reconciliation in this
                period - we recommend processing these before reviewing
                unrealised gains"
          />

          {activeTab === PostUnrealisedFXGainsTab.PayablesOrReceivables && (
            <PayablesOrReceivables
              vehicleId={vehicle._id}
              viewDate={new CalendarDay(debouncedViewDate ?? new Date())}
              currencyRateOverrides={currencyRateOverrides}
              onCurrenciesLoaded={handleCurrenciesLoaded}
              onLastPostingDateLoaded={handleLastPostingDateLoaded}
            />
          )}
          {activeTab === PostUnrealisedFXGainsTab.Cash && (
            <Cash
              vehicleId={vehicle._id}
              currencyRateOverrides={currencyRateOverrides}
              onCurrenciesLoaded={handleCurrenciesLoaded}
            />
          )}
        </PageSection>
      </PageContent>
    </PageContainer>
  );
};

export default PostUnrealisedFXGains;
